import { defineStore } from 'pinia';
import { Category } from '~/models/Category.models';
import type {
  FilterLookup,
  SortMode,
  FilterGroup,
  Filter,
  FilterGroupType,
  FilterGroupTitle,
} from '~/models/Filter.models';
import { updateQueryWithActiveFilters } from '~/utils/filters/filterQueryParams';
import {
  DEFAULT_MONTHLY_PAYMENT_PERIOD,
  DEFAULT_PLAN_ID,
} from '~/utils/pdp/queryParamHelpers';

const MATCHES_NOTHING = -999;
export type SUPPORTED_PRODUCT_CATEGORIES =
  | Category.phones
  | Category.accessory
  | Category.used;

type FilterStoreState = {
  // TODO: handle in productStore instead, automatically?
  productMode: SUPPORTED_PRODUCT_CATEGORIES;
  sortMode: SortMode;
  filters: FilterGroup[];
  activeFilters: FilterLookup;
  searchString: string;
  groupsWithAlternativesChecked: { [groupName: string]: boolean };
  selectedProduct?: ProductCard;
  defaultPlanId: number;
  defaultDownPayment: number;
};

export const useFilterStore = defineStore('filterStore', {
  state: (): FilterStoreState => ({
    productMode: Category.phones,
    sortMode: 'Populärast',
    filters: [],
    activeFilters: {},
    searchString: '',
    groupsWithAlternativesChecked: {},
    selectedProduct: undefined,
    defaultPlanId: Number(DEFAULT_PLAN_ID),
    defaultDownPayment: Number(DEFAULT_MONTHLY_PAYMENT_PERIOD),
  }),
  actions: {
    initializeFilters(
      category: SUPPORTED_PRODUCT_CATEGORIES = Category.phones,
      defaultDownPayment: number,
      defaultPlanId: number,
    ) {
      this.productMode = category;
      // Reset active filters for when store is re-initialized with a different category of products
      this.activeFilters = {};

      const productStore = useProductStore();
      const items = productStore.unwrappedProducts;

      this.filters = [];
      this.searchString = '';

      const manufacturerNamesSet = new Set<string>();
      const categoryNamesSet = new Set<string>();
      for (const item of items) {
        manufacturerNamesSet.add(item.product.ManufacturerName);

        if (item.product.Categories && item.product.Categories.length > 0) {
          for (const category of item.product.Categories) {
            if (category.ParentCategoryId !== 0) {
              categoryNamesSet.add(category.CategoryName);
            }
          }
        }
      }

      const uniqueManufacturerNames = Array.from(manufacturerNamesSet);
      const uniqueCategoryNames = Array.from(categoryNamesSet);

      if (category === Category.accessory) {
        this.filters = filters_createAccessoryFilterOptions(
          uniqueManufacturerNames,
          uniqueCategoryNames,
        );
      } else {
        const isUsed = category === Category.used;
        this.filters = filters_createPhoneFilterOptions(
          uniqueManufacturerNames,
          isUsed,
          defaultPlanId,
          defaultDownPayment,
        );
      }
      const selectedPlan = this.filters
        .filter((group) => group.title == Category.postPaid)
        .flatMap((group) => group.filters)
        .filter((filter) => filter?.default);

      if (
        (category === Category.phones || category === Category.used) &&
        selectedPlan.at(0)?.isAlternative
      ) {
        this.groupsWithAlternativesChecked[Category.postPaid] = true;
      }

      this.defaultPlanId = defaultPlanId;
      this.defaultDownPayment = defaultDownPayment;
      this.__initializeActiveFilter();
    },
    __initializeActiveFilter(): void {
      for (const filter of this.filters) {
        this.activeFilters[filter.title] = {};
      }

      const filterIdsToBeDefaultActive = this.__idsForDefaultActiveFilters;
      for (const id of filterIdsToBeDefaultActive) {
        const [filterGroup, filter] = this.__getGroupAndFilterByFilterId(id);
        this.__toggleValueInActiveFilters(
          filterGroup?.title,
          filterGroup?.type,
          filter?.filterId,
          filter?.name,
        );
      }
    },
    __toggleValueInActiveFilters(
      filterGroupTitle?: FilterGroupTitle,
      inputType: FilterGroupType = 'checkbox',
      idOfFilterToToggle?: string,
      nameOfFilterToToggle?: string,
    ): void {
      if (!filterGroupTitle || !idOfFilterToToggle || !nameOfFilterToToggle) {
        return;
      }

      if (!this.activeFilters[filterGroupTitle]) {
        this.activeFilters[filterGroupTitle] = {};
      }

      if (inputType === 'radio') {
        this.activeFilters[filterGroupTitle] = {
          [idOfFilterToToggle]: nameOfFilterToToggle,
        };
      } else {
        if (idOfFilterToToggle in this.activeFilters[filterGroupTitle]) {
          // Remove this.activeFilters[filterGroupTitle][idOfFilterToToggle] by excluding it
          const filterGroupKeys = Object.keys(
            this.activeFilters[filterGroupTitle],
          ).filter((key) => key !== idOfFilterToToggle);
          const groupedFiltersWithoutTheId: { [key: string]: string } = {};
          for (const key of filterGroupKeys) {
            groupedFiltersWithoutTheId[key] =
              this.activeFilters[filterGroupTitle][key];
          }
          this.activeFilters[filterGroupTitle] = groupedFiltersWithoutTheId;
        } else {
          this.activeFilters[filterGroupTitle][idOfFilterToToggle] =
            nameOfFilterToToggle;
        }
      }
    },
    getFeaturedGroupDisplayText(groupName: FilterGroupTitle): string | null {
      if (!this.activeFilters[groupName]) {
        return null;
      }

      const activeFilterIds = Object.keys(this.activeFilters[groupName]);
      if (activeFilterIds.length === 0) {
        return null;
      }

      const firstId = activeFilterIds[0];
      const group = this.filters.find(
        (filterGroup) => filterGroup.title === groupName,
      );
      const filter = group?.filters?.find(
        (filter) => filter.filterId === firstId,
      );
      return (
        this.__getFeaturedGroupText(
          group?.title,
          filter?.name,
          filter?.isAlternative,
        ) ?? null
      );
    },
    __getFeaturedGroupText(
      groupTitle?: string,
      filterName?: string,
      filterIsAlternative?: boolean,
    ): string | undefined {
      if (this.productMode === Category.accessory) {
        return undefined;
      }

      switch (groupTitle) {
        case Category.postPaid:
          if (filterIsAlternative) {
            return filterName;
          }
          return filterName + ' surf';
        case 'Delbetalning':
          if (filterName !== 'Direktbetalning') return 'Betala i ' + filterName;
          return filterName;
        default:
          return undefined;
      }
    },
    isAlternativesChecked(groupName: string) {
      return !!this.groupsWithAlternativesChecked[groupName];
    },
    toggle(filterId: string | undefined): void {
      const [filterGroup, filter] =
        this.__getGroupAndFilterByFilterId(filterId);
      this.__toggleValueInActiveFilters(
        filterGroup?.title,
        filterGroup?.type,
        filter?.filterId,
        filter?.name,
      );

      updateQueryWithActiveFilters();
    },
    toggleAlternative(groupName: string): void {
      if (groupName in this.groupsWithAlternativesChecked) {
        // TODO: set undefined or something instead of delete better?
        delete this.groupsWithAlternativesChecked[groupName];
      } else {
        this.groupsWithAlternativesChecked[groupName] = true;
      }
    },
    clearFilters(): void {
      for (const filterGroup of this.filters) {
        if (filterGroup.type !== 'radio') {
          // TODO: set undefined or something instead of delete better?
          delete this.activeFilters[filterGroup.title];
        }
      }
    },
    clearGroup(groupName: FilterGroupTitle): void {
      this.activeFilters[groupName] = {};
    },
    __getGroupAndFilterByFilterId(
      filterId: string | undefined,
    ): [FilterGroup?, Filter?] {
      const filterGroup = this.filters.find((group) =>
        group.filters?.find((filter) => filter.filterId === filterId),
      );
      const filter = filterGroup?.filters?.find(
        (filter) => filter && filter.filterId === filterId,
      );
      return [filterGroup, filter];
    },
  },
  getters: {
    products(): ProductCard[] {
      return this.__sortedFilteredProducts;
    },
    featuredFilters(state: FilterStoreState): (Filter | undefined)[] {
      return state.filters
        .flatMap((group) => group.filters)
        .filter((filter) => filter?.isFeatured);
    },
    __sortedFilteredProducts(): ProductCard[] {
      const filtered = this.filteredProducts;
      return this.productMode === Category.accessory
        ? filters_sortAccesoryProductCards(filtered, this.sortMode)
        : filters_sortPhonesProductCards(filtered, this.sortMode);
    },
    __idsForDefaultActiveFilters(state: FilterStoreState): string[] {
      return state.filters
        .flatMap((group) => group.filters)
        .filter((filter) => filter?.default)
        .map((filter) => filter?.filterId)
        .filter((id: string | undefined) => id !== undefined);
    },
    phonesFilteredBySettings(state: FilterStoreState): ProductCard[] {
      if (state.productMode === Category.accessory) {
        return [];
      }

      if (
        !state.activeFilters ||
        Object.keys(state.activeFilters).length === 0
      ) {
        return [];
      }

      const productStore = useProductStore();
      const items = productStore.unwrappedProducts;

      const matchDigitsExpression = /\d+/;

      let subscriptionSettingString = '';
      const subscriptionActiveFilter = state.activeFilters['Abonnemang'];
      if (
        subscriptionActiveFilter &&
        Object.keys(subscriptionActiveFilter).length > 0
      ) {
        subscriptionSettingString = Object.values(subscriptionActiveFilter)[0];
      }

      const subscriptionAllowance = +(
        subscriptionSettingString?.match(matchDigitsExpression)?.[0] ??
        MATCHES_NOTHING
      );

      const isStudent = subscriptionSettingString
        ?.toLowerCase()
        ?.includes('student');

      let nrPaymentsString = '';
      const partialPaymentActiveFilter = state.activeFilters['Delbetalning'];
      if (
        partialPaymentActiveFilter &&
        Object.keys(partialPaymentActiveFilter).length > 0
      ) {
        nrPaymentsString = Object.values(partialPaymentActiveFilter)[0];
      }

      const nrPayments = +(
        nrPaymentsString?.match(matchDigitsExpression)?.[0] ?? 1
      );

      const excludeItem = (item: ProductCard) => {
        if (!item || !item.bundlePrice) {
          return true;
        }
        const isDirectPayment = nrPayments === 1;
        const noBindingPeriod = item.bundlePrice.BindingPeriod.Months === 0;

        if (
          (item.bundlePrice.Plan?.RegularDataAllowance ||
            item.bundlePrice.Plan?.DataAllowance) !== subscriptionAllowance
        ) {
          return true;
        }

        if (nrPayments !== item.payment.Time) {
          return true;
        }

        if (isDirectPayment && !noBindingPeriod) {
          return true;
        }

        if (!isDirectPayment && noBindingPeriod) {
          return true;
        }

        if (isStudent !== item.bundlePrice.Plan?.IsStudent) {
          return true;
        }

        return false;
      };
      const filteredBySettings = items.filter((item) => !excludeItem(item));

      return filteredBySettings;
    },
    numberOfFilteredProducts(): number {
      return this.filteredProducts.length;
    },
    filteredProducts(state: FilterStoreState): ProductCard[] {
      const productStore = useProductStore();
      const lowerCaseSearchString = state.searchString.toLowerCase();

      let productCardsToFilter: ProductCard[] = [];

      if (state.productMode === Category.accessory) {
        const items = productStore.unwrappedProducts;
        productCardsToFilter = items.slice();
      } else {
        productCardsToFilter = this.phonesFilteredBySettings.slice();
      }

      if (productCardsToFilter.length === 0) {
        return [];
      }

      const filtered = productCardsToFilter.filter((productCard) => {
        const combineProductNameAndManufacturer =
          productCard.product.ManufacturerName.toLocaleLowerCase() +
          ' ' +
          productCard.product.ProductName.toLocaleLowerCase();

        const matchesSearchFieldFilter =
          combineProductNameAndManufacturer.includes(lowerCaseSearchString);

        let filterManufacture = false;
        let filterCondition = false;
        let filterCategory = false;

        filterManufacture =
          Object.keys(this.activeFilters).find((item) => {
            if (
              (item === 'Tillverkare' &&
                this.activeFilters[item] &&
                Object.values(this.activeFilters[item]).includes(
                  productCard.product.ManufacturerName,
                )) ||
              (item === 'Tillverkare' &&
                this.activeFilters[item] &&
                Object.keys(this.activeFilters[item]).length <= 0)
            ) {
              return true;
            }

            return false;
          }) !== undefined;

        filterCondition =
          Object.keys(this.activeFilters).find((item) => {
            if (
              (item === 'Begagnat skick' &&
                this.activeFilters[item] &&
                Object.values(this.activeFilters[item]).includes(
                  productCard.variant.Condition,
                )) ||
              (item === 'Begagnat skick' &&
                this.activeFilters[item] &&
                Object.keys(this.activeFilters[item]).length <= 0)
            ) {
              return true;
            }

            return false;
          }) !== undefined;

        filterCategory =
          Object.keys(this.activeFilters).find((item) => {
            if (
              (item === 'Kategorier' &&
                productCard.product.Categories.some(
                  (category) =>
                    this.activeFilters[item] &&
                    Object.values(this.activeFilters[item]).includes(
                      category.CategoryName,
                    ),
                )) ||
              (item === 'Kategorier' &&
                this.activeFilters[item] &&
                Object.keys(this.activeFilters[item]).length <= 0)
            ) {
              return true;
            }

            return false;
          }) !== undefined;

        if (this.productMode === Category.accessory) {
          return (
            matchesSearchFieldFilter && filterManufacture && filterCategory
          );
        } else if (this.productMode === Category.used) {
          return (
            matchesSearchFieldFilter && filterManufacture && filterCondition
          );
        }
        return matchesSearchFieldFilter && filterManufacture;
      });

      return filtered;
    },
    isFilterActiveById(state: FilterStoreState): { [key: string]: boolean } {
      const activeFiltersById: { [key: string]: boolean } = {};

      if (
        !state.activeFilters ||
        Object.keys(state.activeFilters).length === 0
      ) {
        return activeFiltersById;
      }

      const activeFilterObjects = Object.values(state.activeFilters);
      for (const filterObject of activeFilterObjects) {
        const filterIds = Object.keys(filterObject);
        for (const id of filterIds) {
          activeFiltersById[id] = true;
        }
      }

      return activeFiltersById;
    },
    filterGroupTitleByFilterIdMap: (
      state: FilterStoreState,
    ): { [key: string]: string } => {
      return state.filters.reduce(
        (map: { [key: string]: string }, filterGroup) => {
          if (filterGroup.filters) {
            filterGroup.filters.forEach((filter) => {
              map[filter.filterId] = filterGroup.title;
            });
          }
          return map;
        },
        {},
      );
    },
    filterValueNameByFilterIdMap: (
      state: FilterStoreState,
    ): { [key: string]: string } => {
      return state.filters.reduce(
        (map: { [key: string]: string }, filterGroup) => {
          if (filterGroup.filters) {
            filterGroup.filters.forEach((filter) => {
              map[filter.filterId] = filter.name;
            });
          }
          return map;
        },
        {},
      );
    },
  },
});
